import { inject, Injectable, signal } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import {
  DsmPackageAddProductsPayload,
  DsmPackageCopyPreviewResult,
  DsmPackageCopyTarget,
  DsmPackageCopyTargetResult,
  DsmPackageCreatePayload,
  DsmPackageDetail,
  DsmPackageFilters,
  DsmPackageImportFromPackagePayload,
  DsmPackageListItem,
  DsmPackageProjectModelElevationOption,
  DsmPackageUpdatePayload
} from '../models/dsm-package.models';

@Injectable({ providedIn: 'root' })
export class DsmPackageService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #apiConfig = inject(API_CONFIG);
  readonly baseUrl = this.#apiConfig.baseUrlV2;

  readonly refreshTrigger = signal(0);

  triggerRefresh(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  getPackages(filters?: DsmPackageFilters, showLoader = true): Observable<DsmPackageListItem[]> {
    const params = new URLSearchParams();
    if (filters?.projectId) params.set('projectId', filters.projectId);
    if (filters?.modelId) params.set('modelId', filters.modelId);
    if (filters?.elevationId) params.set('elevationId', filters.elevationId);
    if (filters?.isActive !== undefined) params.set('isActive', filters.isActive ? 'true' : 'false');
    const query = params.toString();
    return this.#api
      .getData<DsmPackageListItem[] | null>(`${this.baseUrl}/packages${query ? `?${query}` : ''}`, {
        showLoader,
        showMessage: false,
        showError: showLoader
      })
      .pipe(map(items => items ?? []));
  }

  getPackageById(id: string, showLoader = true): Observable<DsmPackageDetail> {
    return this.#api.getData<DsmPackageDetail>(`${this.baseUrl}/packages/${id}`, {
      showLoader,
      showMessage: false,
      showError: showLoader
    });
  }

  createPackage(payload: DsmPackageCreatePayload): Observable<DsmPackageDetail> {
    return this.#api.postData<DsmPackageDetail>(
      `${this.baseUrl}/packages`,
      { showLoader: true, showMessage: true, message: 'Package created successfully' },
      payload
    );
  }

  updatePackage(id: string, payload: DsmPackageUpdatePayload): Observable<void> {
    return this.#api.putData<void>(`${this.baseUrl}/packages/${id}`, payload, {
      showLoader: true,
      showMessage: true,
      message: 'Package updated successfully'
    });
  }

  deletePackage(id: string): Observable<void> {
    return this.#api.deleteData<void>(`${this.baseUrl}/packages/${id}`, {
      showLoader: true,
      showMessage: true,
      message: 'Package deleted successfully'
    });
  }

  addPackageProducts(id: string, payload: DsmPackageAddProductsPayload): Observable<void> {
    return this.#api.postData<void>(
      `${this.baseUrl}/packages/${id}/products`,
      { showLoader: true, buttonId: 'dsm-save-options', showMessage: false, showError: false },
      payload
    );
  }

  importFromPackage(payload: DsmPackageImportFromPackagePayload): Observable<void> {
    return this.#api.postData<void>(
      `${this.baseUrl}/packages/copy-products`,
      { showLoader: true, showMessage: true, message: 'Package imported successfully' },
      payload
    );
  }

  deletePackageProduct(packageId: string, packageProductId: string): Observable<void> {
    return this.#api.deleteData<void>(`${this.baseUrl}/packages/${packageId}/products/${packageProductId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Option removed from package'
    });
  }

  updatePackageProductCount(packageId: string, packageProductId: string, count: number): Observable<DsmPackageDetail> {
    return this.#api.putData<DsmPackageDetail>(
      `${this.baseUrl}/packages/${packageId}/products/${packageProductId}`,
      { count },
      { showLoader: false, showMessage: false }
    );
  }

  getProjectModelElevations(projectId: string): Observable<DsmPackageProjectModelElevationOption[]> {
    return this.#api
      .getData<
        DsmPackageProjectModelElevationOption[] | null
      >(`${this.baseUrl}/Project/${projectId}/models/elevations`, { showLoader: false, showMessage: false })
      .pipe(map(items => items ?? []));
  }

  previewCopyPackage(
    packageId: string,
    targets: DsmPackageCopyTarget[]
  ): Observable<DsmPackageCopyPreviewResult | null> {
    return this.#api.postData<DsmPackageCopyPreviewResult>(
      `${this.baseUrl}/packages/${packageId}/copy/preview`,
      { showLoader: false, showMessage: false, showError: false },
      { targets }
    );
  }

  copyPackage(packageId: string, targets: DsmPackageCopyTarget[]): Observable<DsmPackageCopyTargetResult[]> {
    return this.#api
      .postData<{
        results: DsmPackageCopyTargetResult[];
      }>(
        `${this.baseUrl}/packages/${packageId}/copy`,
        { showLoader: true, showMessage: false, showError: false },
        { targets }
      )
      .pipe(map(response => response?.results ?? []));
  }

  /** Matches backend DiscountCalculator.AmountFromPercentage (2dp, away from zero for positives). */
  discountAmountFromPercentage(amount: number, percentage: number | null | undefined): number {
    const pct = Number(percentage) || 0;
    const base = Number(amount) || 0;
    if (pct <= 0 || base === 0) return 0;
    return Math.round(((base * pct) / 100) * 100 + Number.EPSILON) / 100;
  }

  amountAfterDiscount(amount: number, percentage: number | null | undefined): number {
    return Math.max(0, (Number(amount) || 0) - this.discountAmountFromPercentage(amount, percentage));
  }
}
